import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Grid, Chip, Box, Divider,
} from '@mui/material';
import { Expense, ExpenseStatus } from '../../types';
import { toUploadUrl } from '../../utils/uploadUrl';
import CopyableIdCell from '../common/CopyableIdCell';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

interface Props {
  open: boolean;
  expense: Expense | null;
  onClose: () => void;
}

type ChipColor = 'warning' | 'success' | 'error' | 'default';
const statusConfig: Record<ExpenseStatus, { color: ChipColor; label: string }> = {
  [ExpenseStatus.PENDING]: { color: 'warning', label: 'Pending' },
  [ExpenseStatus.APPROVED]: { color: 'success', label: 'Approved' },
  [ExpenseStatus.REJECTED]: { color: 'error', label: 'Rejected' },
};

const ViewExpenseDialog: React.FC<Props> = ({ open, expense, onClose }) => {
  const money = useCurrencyFormat();
  if (!expense) return null;

  // Reversed is a derived state — row still carries status=APPROVED but the
  // isActive flag is false. isActive alone is sufficient (deleteExpense
  // refuses non-PENDING, so an inactive APPROVED row can only have come
  // from a reversal). Older reversals that predate the audit fields will
  // still be flagged correctly; their "Reversed By" block just shows
  // Unknown/no reason.
  const isReversed =
    expense.status === ExpenseStatus.APPROVED && expense.isActive === false;
  const config = isReversed
    ? { color: 'default' as ChipColor, label: 'Reversed' }
    : statusConfig[expense.status];
  const docUrl = expense.documentFilename
    ? toUploadUrl('expense-documents', expense.documentFilename)
    : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Expense Details
        <Chip label={config.label} color={config.color} size="small" />
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid size={12}>
            <Typography variant="subtitle2" color="text.secondary">Title</Typography>
            <Typography variant="body1">{expense.title}</Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
            <Typography variant="h6" color="primary">{money(expense.amount)}</Typography>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">Date</Typography>
            <Typography variant="body1">{new Date(expense.date).toLocaleDateString()}</Typography>
          </Grid>

          {expense.categoryId && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">Category</Typography>
              <Typography variant="body1">{expense.categoryId.name}</Typography>
            </Grid>
          )}

          {expense.subCategoryId && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" color="text.secondary">Sub-Category</Typography>
              <Typography variant="body1">{expense.subCategoryId.name}</Typography>
            </Grid>
          )}

          {expense.notes && (
            <Grid size={12}>
              <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{expense.notes}</Typography>
            </Grid>
          )}

          <Grid size={12}>
            <Typography variant="subtitle2" color="text.secondary">Submitted By</Typography>
            <Typography variant="body1">{expense.createdBy.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(expense.createdAt).toLocaleString()}
            </Typography>
          </Grid>

          {expense.status === ExpenseStatus.APPROVED && expense.approvedBy && (
            <>
              <Grid size={12}><Divider /></Grid>
              <Grid size={12}>
                <Typography variant="subtitle2" color="success.main">Approved By</Typography>
                <Typography variant="body1">{expense.approvedBy.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {expense.approvedAt && new Date(expense.approvedAt).toLocaleString()}
                </Typography>
              </Grid>
            </>
          )}

          {isReversed && (
            <>
              <Grid size={12}><Divider /></Grid>
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary">Reversed By</Typography>
                <Typography variant="body1">{expense.reversedBy?.name || 'Unknown'}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {expense.reversedAt && new Date(expense.reversedAt).toLocaleString()}
                </Typography>
                {expense.reversalReason && (
                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    Reason: {expense.reversalReason}
                  </Typography>
                )}
              </Grid>
            </>
          )}

          {expense.status === ExpenseStatus.REJECTED && expense.rejectedBy && (
            <>
              <Grid size={12}><Divider /></Grid>
              <Grid size={12}>
                <Typography variant="subtitle2" color="error.main">Rejected By</Typography>
                <Typography variant="body1">{expense.rejectedBy.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {expense.rejectedAt && new Date(expense.rejectedAt).toLocaleString()}
                </Typography>
                {expense.rejectionReason && (
                  <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    Reason: {expense.rejectionReason}
                  </Typography>
                )}
              </Grid>
            </>
          )}

          {docUrl && (
            <>
              <Grid size={12}><Divider /></Grid>
              <Grid size={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Receipt / Document
                </Typography>
                {expense.documentType === 'image' ? (
                  <Box
                    component="img"
                    src={docUrl}
                    alt={expense.documentName}
                    sx={{ maxWidth: '100%', maxHeight: 300, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                  />
                ) : (
                  <Button variant="outlined" href={docUrl} target="_blank" rel="noopener noreferrer">
                    View PDF: {expense.documentName}
                  </Button>
                )}
              </Grid>
            </>
          )}

          {expense.batchId && (
            <Grid size={12}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Typography variant="caption" color="text.secondary">Batch ID:</Typography>
                <CopyableIdCell value={expense.batchId} variant="caption" fontWeight={400} truncate />
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewExpenseDialog;
